import React, { useState, useMemo, useCallback, forwardRef } from "react";
import { groupBy } from "lodash";
import {
    InputAdornment,
    List,
    ListItem,
    ListSubheader,
    ListItemText,
    Popover,
    TextField,
    Toolbar,
    Typography
} from "@mui/material/";
import { ArrowDropDown } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { strings } from "../../i18n";
import SearchBox from "../SearchBox";
import styles from "./styles";

const MIN_SEARCH_ITEMS_COUNT = 1;

const anchorOrigin = {
    vertical: "center",
    horizontal: "center"
};

const transformOrigin = {
    vertical: "center",
    horizontal: "center"
};

type Props = {
    disabled: boolean,
    error: string,
    groupByField: string,
    items: Array<any>,
    onChange: Function,
    onClose: Function,
    labelField: string,
    title: string,
    value: Array<any>,
    valueField: string,
    valuePrimitive: boolean
};

/**
 * Select All Component
 */
export default ({
    disabled = false,
    error,
    groupByField,
    items = [],
    onChange,
    onClose,
    labelField = "name",
    title = "",
    value = [],
    valueField = "id",
    valuePrimitive = false
}: Props) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const classes = makeStyles(styles)();
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const selectRef = null;

    const allSelected = useMemo(() => {
        const next = items.filter((item) => !item.disabled);
        return value && value.length === next.length;
    }, [items, value]);

    const getLabel = (item: any) => (valuePrimitive ? item : item[labelField]);
    const getValue = (item: any) => (valuePrimitive ? item : item[valueField]);

    const data = useMemo(() => {
        let next = items;
        if (searchText) {
            const regex = new RegExp(searchText, "ig");
            next = items.filter((item) => getLabel(item).match(regex));
        }
        return Boolean(groupByField) ? groupBy(next, groupByField) : next;
    }, [groupByField, items, searchText]);

    const helperText = useMemo(() => {
        if (error) {
            return error;
        }
        if (value.length) {
            return `${strings("selected")} \u2014 ${value.length}/${
                items.length
            }`;
        }
        return "";
    }, [error, items, value]);

    const handleChange = (item: any) => () => {
        const itemValue = getValue(item);
        const next = value.includes(itemValue)
            ? value.filter((i) => i !== itemValue)
            : [...value, itemValue];
        onChange && onChange(next);
    };

    const onSelectAll = () => {
        let next = [];
        if (!allSelected) {
            next = items.filter((item) => !item.disabled).map(getValue);
        }
        onChange && onChange(next);
    };

    const onSelectClose = () => {
        setOpen(false);
        setSearchText("");
        onClose && onClose();
    };

    const onSelectOpen = (event: Event) => {
        if (!disabled && items.length) {
            setAnchorEl(event.currentTarget);
            setOpen(true);
        }
    };

    const onRenderItem = (item: any, index: number) => {
        const itemValue = getValue(item);
        return (
            <ListItem
                button
                disabled={item.disabled}
                key={index}
                onClick={handleChange(item)}
                selected={value.includes(itemValue)}
                value={itemValue}
            >
                <ListItemText primary={getLabel(item)} />
            </ListItem>
        );
    };

    const renderValue = useCallback(() => {
        const selected = items.filter((item) => value.includes(getValue(item)));
        if (selected.length === 0) return "";
        return getLabel(selected[0]);
    }, [items, value]);

    return (
        <>
            <TextField
                disabled={disabled || items.length === 0}
                error={Boolean(error)}
                helperText={helperText}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <ArrowDropDown />
                        </InputAdornment>
                    ),
                    inputComponent: forwardRef(() => (
                        <>
                            <Typography
                                ref={selectRef}
                                className={classes.input}
                                variant="body2"
                                noWrap
                            >
                                {renderValue() || (
                                    <span className={classes.dummy}>dummy</span>
                                )}
                            </Typography>
                            <div className={classes.flexGrow} />
                        </>
                    )),
                    readOnly: true
                }}
                InputLabelProps={{ shrink: Boolean(value && value.length) }}
                label={title || strings("select")}
                onClick={onSelectOpen}
            />
            <Popover
                anchorEl={anchorEl}
                anchorOrigin={anchorOrigin}
                onClose={onSelectClose}
                open={open}
                transformOrigin={transformOrigin}
            >
                <List>
                    {items.length > MIN_SEARCH_ITEMS_COUNT && (
                        <ListItem
                            className={classes.selectAll}
                            onClick={onSelectAll}
                        >
                            {strings(allSelected ? "none" : "all")}
                        </ListItem>
                    )}
                    {Boolean(groupByField)
                        ? Object.entries(data).map(([k, v]) => (
                              <React.Fragment key={k}>
                                  <ListSubheader>{k}</ListSubheader>
                                  {v.map(onRenderItem)}
                              </React.Fragment>
                          ))
                        : data.map(onRenderItem)}
                </List>
                <Toolbar className={classes.toolbar}>
                    <SearchBox
                        onValueChange={setSearchText}
                        value={searchText}
                    />
                </Toolbar>
            </Popover>
        </>
    );
};
